/**
 * "Go check your email": the pop-up that opens right after a successful signup on the homepage, the research articles
 * and Ask Aelo (which submits the page's own form). Owner, 2026-10-08: "We should put a pop up that literally says 'Go
 * check your email' so it's overwhelmingly obvious."
 *
 * Owner, 2026-10-12 (the confirmation and sharing plan): most signups happen inside Facebook's own browser on a phone and
 * never come back from the email. So a new signup confirms right here, by typing the 6-digit code from the email (it is
 * in the subject line, so the phone's notification shows it), and the same pop-up then becomes the share step: "You're
 * in! Now get free stuff." Simple words and big targets throughout: most of the audience is 65 or older.
 *
 * - It opens only when the server says an email was sent (owner rule 2026-09-18: never say "sent" when nothing was). The
 *   form's own status line and links are unchanged underneath it.
 * - Self-contained (no imports) and in its own shadow root, so it ships inside the signup adapter without changing any
 *   shared bundle, and no page's CSS can restyle it.
 * - The cookie notice and privacy controls (z-index 100 and 101) stay above it, stay usable, and the card is laid out
 *   above them, so the cookie choice is never covered. Ask Aelo (95) and the site header stay underneath.
 * - Inside the Facebook and Instagram in-app browsers the inbox button opens the mail APP (the web inbox there asks
 *   people to sign in); if the app does not open, the pop-up says what to do instead of leaving the page.
 * - Accessibility: role=dialog with aria-modal, named by the headline and described by the line. Focus moves to the code
 *   box (or the main button) and Tab stays inside; Esc, the × and the backdrop close it; the rest of the page is inert
 *   meanwhile. Focus then returns to the form's status line (or to the dialog the person joined from, such as Ask Aelo).
 * - Phones first; the entrance motion runs only when reduced motion isn't requested.
 */

export type InboxProvider = 'gmail' | 'outlook' | 'yahoo' | 'icloud' | 'aol'
export type ResendOutcome = { outcome: 'sent' } | { outcome: 'not_sent' | 'failed'; message: string }
export type CodeOutcome =
  | { outcome: 'ok' }
  | { outcome: 'invalid'; attemptsLeft: number | null }
  | { outcome: 'expired' }
  | { outcome: 'limited'; retryAt: string | null }
  | { outcome: 'failed'; message: string }
/** What the share step needs: the member's invite link, the ready-made message (link included) and their page. */
export type ShareStep = { link: string; message: string; pageUrl: string }
export type ShareChannel = 'native' | 'sms' | 'copy'

export type CheckEmailOptions = {
  email: string
  member: 'new' | 'existing'
  /** Focus goes here when the pop-up closes (the submitted form's status line)... */
  returnFocus: HTMLElement
  /** ...unless the person joined from somewhere else on the page that can take focus back (e.g. the Ask Aelo panel). */
  origin: Element | null
  onShown?: (provider: InboxProvider | 'none') => void
  onInbox?: (provider: InboxProvider) => void
  /** Sends the email again through the page's existing request. Resolves null when it could not start (busy). */
  onResend?: () => Promise<ResendOutcome | null>
  /** A new signup confirms here by typing the 6-digit code from the email. Without it, the pop-up only points to the inbox. */
  onVerify?: (code: string) => Promise<CodeOutcome>
  /** After a correct code: the member's link and message for the share step (null: point to their page instead). */
  onConfirmed?: () => Promise<ShareStep | null>
  onShare?: (channel: ShareChannel) => void
  onShareShown?: () => void
  /** For tests; defaults to the browser's user agent. */
  userAgent?: string
}

// Owner, 2026-10-12: "free" is allowed again; simple, exciting words; "so stupidly simple... a monkey could understand it".
const COPY = {
  title: 'Go check your email',
  lineCode: ['We just sent a 6-digit code to ', '.'],
  lineNew: ['We sent a confirmation link to ', '. Tap it to get your free invite link.'],
  lineExisting: ['We sent a link to ', '. Tap it to open your page.'],
  codeLabel: 'Type the code here',
  confirm: 'Confirm',
  checking: 'Checking…',
  codeHint: 'The code is in the email’s subject line. You can also tap the button inside the email.',
  wrongCode: 'That code doesn’t match. Check the newest email from Aelo and try again.',
  oneTryLeft: '1 try left.',
  triesLeft: ' tries left.',
  expired: 'That code has expired. Tap “Send a new code”.',
  limited: 'Too many tries. Wait a few minutes, then try again.',
  failed: 'We couldn’t check that code. Check your connection and try again.',
  gotIt: 'Got it',
  hint: 'Not there? Check Promotions or Spam.',
  resend: 'Send it again',
  resendCode: 'Send a new code',
  sending: 'Sending…',
  resent: 'Sent. Check your inbox again.',
  appMissing: 'We couldn’t open the app. Open your email yourself, find the email from Aelo, then come back here.',
  newTab: '(opens in a new tab)',
  close: 'Close',
  doneTitle: 'You’re in!',
  doneLine: 'Now get free gifts. Send your link to 5 friends and get $10 Aelo Cash.',
  doneNoLink: 'Your free invite link is waiting on your page.',
  doneCount: '0 of 5 friends',
  linkLabel: 'Your free invite link',
  shareText: 'Text my link to friends',
  copyLink: 'Copy my link',
  copied: 'Copied. Paste it into a text or an email.',
  copyFailed: 'Press and hold the link to copy it.',
  myPage: 'Go to my page',
} as const

const INBOXES: Record<InboxProvider, { label: string; url: string }> = {
  gmail: { label: 'Open Gmail', url: 'https://mail.google.com/mail/u/0/#inbox' },
  outlook: { label: 'Open Outlook', url: 'https://outlook.live.com/mail/' },
  yahoo: { label: 'Open Yahoo Mail', url: 'https://mail.yahoo.com/' },
  icloud: { label: 'Open iCloud Mail', url: 'https://www.icloud.com/mail' },
  aol: { label: 'Open AOL Mail', url: 'https://mail.aol.com/' },
}

/** The mail apps the in-app browsers can hand off to: an iOS URL scheme, or an Android package for an intent link. */
const APPS: Partial<Record<InboxProvider, { label: string; ios?: string; android?: string }>> = {
  gmail: { label: 'Open the Gmail app', ios: 'googlegmail://', android: 'com.google.android.gm' },
  outlook: { label: 'Open the Outlook app', ios: 'ms-outlook://', android: 'com.microsoft.office.outlook' },
  yahoo: { label: 'Open the Yahoo Mail app', ios: 'ymail://', android: 'com.yahoo.mobile.client.android.mail' },
  icloud: { label: 'Open the Mail app', ios: 'message://' },
}

/** The webmail inbox for a recognizable personal address; null for every other domain (work and custom domains). */
export function inboxFor(email: string): { provider: InboxProvider; label: string; url: string } | null {
  const domain = email.trim().toLowerCase().replace(/\.$/, '').split('@').pop() ?? ''
  const country = '(?:com|[a-z]{2}|co\\.[a-z]{2}|com\\.[a-z]{2})'
  let provider: InboxProvider | null = null
  if (/^(?:gmail|googlemail)\.com$/.test(domain)) provider = 'gmail'
  else if (new RegExp(`^(?:outlook|hotmail|live)\\.${country}$`).test(domain) || domain === 'msn.com') provider = 'outlook'
  else if (new RegExp(`^yahoo\\.${country}$`).test(domain) || domain === 'ymail.com' || domain === 'rocketmail.com') provider = 'yahoo'
  else if (/^(?:icloud|me|mac)\.com$/.test(domain)) provider = 'icloud'
  else if (domain === 'aol.com') provider = 'aol'
  return provider ? { provider, ...INBOXES[provider] } : null
}

/** Facebook's and Instagram's own browsers (where most ad visitors sign up). Their web inboxes ask people to sign in. */
export function inAppBrowser(userAgent: string): boolean {
  return /\b(?:FBAN|FBAV|FB_IAB|FBIOS|FB4A|Instagram)\b/i.test(userAgent)
}

function platformOf(userAgent: string): 'ios' | 'android' | null {
  if (/\b(?:iPhone|iPad|iPod)\b/i.test(userAgent)) return 'ios'
  if (/\bAndroid\b/i.test(userAgent)) return 'android'
  return null
}

/** The inbox button: the mail app inside an in-app browser on a phone, the web inbox everywhere else. */
export function inboxTarget(email: string, userAgent: string): { provider: InboxProvider; label: string; url: string; app: string | null } | null {
  const inbox = inboxFor(email)
  if (!inbox) return null
  const platform = platformOf(userAgent), app = APPS[inbox.provider]
  if (!app || !platform || !inAppBrowser(userAgent)) return { ...inbox, app: null }
  const link = platform === 'ios'
    ? app.ios
    : app.android && `intent://#Intent;package=${app.android};action=android.intent.action.MAIN;category=android.intent.category.LAUNCHER;S.browser_fallback_url=${encodeURIComponent(inbox.url)};end`
  return link ? { ...inbox, label: app.label, app: link } : { ...inbox, app: null }
}

/** Digits only, at most six: what people type, paste or get from the phone's code suggestion. */
export function cleanCode(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6)
}

const STYLE = `
:host{all:initial}
.ce{position:fixed;inset:0;z-index:98;display:flex;align-items:center;justify-content:center;box-sizing:border-box;padding:16px calc(var(--ce-reserve-right,0px) + 16px) calc(var(--ce-reserve,0px) + 16px) 16px;font-family:Manrope,'Avenir Next',system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;font-size:16px;line-height:1.5;color:#092e3e;-webkit-text-size-adjust:100%;text-size-adjust:100%;-webkit-font-smoothing:antialiased}
.backdrop{position:absolute;inset:0;background:rgba(9,46,62,.6);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px)}
.card{position:relative;box-sizing:border-box;width:100%;max-width:448px;max-height:100%;overflow-y:auto;overscroll-behavior:contain;padding:48px 24px 22px;background:#fff;text-align:center;box-shadow:0 28px 70px -18px rgba(9,46,62,.55),0 2px 8px rgba(9,46,62,.14);outline:0}
.icon{display:grid;place-items:center;width:64px;height:64px;margin:0 auto;border-radius:50%;background:#e7eef0;color:#092e3e}
.icon svg{width:30px;height:30px}
.icon[data-done]{background:#d9f0e2;color:#0f5a32}
.title{margin:18px 0 12px;font-size:clamp(34px,10vw,46px);font-weight:700;line-height:1.04;letter-spacing:-.045em;text-wrap:balance;color:#092e3e}
.line{margin:0 auto;max-width:30em;font-size:18px;line-height:1.5;color:#2b4752;text-wrap:pretty}
.line .addr{display:inline-block;max-width:100%;overflow-wrap:anywhere}
.line strong{font-weight:700;color:#092e3e}
.primary{display:flex;align-items:center;justify-content:center;gap:10px;box-sizing:border-box;width:100%;min-height:58px;margin:24px 0 0;padding:14px 20px;border:0;border-radius:0;background:#092e3e;color:#fff;font-family:inherit;font-size:18px;font-weight:700;line-height:1.2;letter-spacing:-.005em;text-decoration:none;cursor:pointer;-webkit-tap-highlight-color:transparent;transition:background-color .15s}
.primary svg{width:18px;height:18px;flex:none}
.primary:hover{background:#164b60}
.primary:disabled{opacity:.45;cursor:default}
.secondary{display:flex;align-items:center;justify-content:center;gap:10px;box-sizing:border-box;width:100%;min-height:54px;margin:12px 0 0;padding:12px 18px;border:2px solid #092e3e;border-radius:0;background:#fff;color:#092e3e;font-family:inherit;font-size:17px;font-weight:700;line-height:1.2;text-decoration:none;cursor:pointer;-webkit-tap-highlight-color:transparent}
.secondary svg{width:17px;height:17px;flex:none}
.secondary:hover{background:#f1f5f6}
.code{margin:22px 0 0;text-align:left}
.code label{display:block;margin:0 0 8px;font-size:17px;font-weight:700;color:#092e3e}
.code input{box-sizing:border-box;width:100%;min-height:68px;margin:0;padding:8px 12px;border:2px solid #092e3e;border-radius:0;background:#fff;color:#092e3e;font-family:inherit;font-size:36px;font-weight:700;letter-spacing:.32em;text-align:center;font-variant-numeric:tabular-nums;caret-color:#092e3e}
.code input::placeholder{color:#b9c6ca;letter-spacing:.32em}
.code input:focus{outline:3px solid #2f7d55;outline-offset:2px}
.code input[aria-invalid=true]{border-color:#8c1d2e}
.code .primary{margin-top:12px}
.hint{margin:14px 0 0;font-size:15px;line-height:1.45;color:#4a6670}
.note{margin:12px 0 0;padding:12px 14px;background:#fff6e5;color:#5c3b00;font-size:15px;line-height:1.45;text-align:left}
.note:empty{display:none}
.more{margin:18px 0 0;padding-top:12px;border-top:1px solid #e3e9eb}
.resend{display:inline-flex;align-items:center;min-height:44px;margin:0;padding:0 10px;border:0;background:none;color:#092e3e;font-family:inherit;font-size:16px;font-weight:700;line-height:1.2;text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:4px;cursor:pointer;-webkit-tap-highlight-color:transparent}
.resend:disabled{opacity:.6;cursor:wait}
.status{margin:8px 0 0;font-size:16px;line-height:1.45;color:#2b4752;overflow-wrap:anywhere}
.status:empty{display:none}
.status[data-tone=error]{color:#8c1d2e;font-weight:600}
.dots{display:flex;justify-content:center;gap:10px;margin:20px 0 6px}
.dots span{width:22px;height:22px;border-radius:50%;border:2px solid #2f7d55;background:#fff}
.count{margin:0;font-size:15px;font-weight:700;color:#2f7d55;letter-spacing:.02em}
.link-field{box-sizing:border-box;width:100%;min-height:50px;margin:16px 0 0;padding:10px 12px;border:1px solid #c7d3d6;background:#f6f9fa;color:#092e3e;font-family:inherit;font-size:16px;text-align:center}
.page-link{display:inline-flex;align-items:center;min-height:48px;margin:10px 0 0;color:#092e3e;font-size:17px;font-weight:700;text-decoration:underline;text-underline-offset:4px}
.close{position:absolute;top:6px;right:6px;display:grid;place-items:center;width:44px;height:44px;margin:0;padding:0;border:0;background:none;color:#092e3e;cursor:pointer;-webkit-tap-highlight-color:transparent}
.close svg{width:22px;height:22px}
.close:hover{color:#164b60}
.primary:focus-visible,.secondary:focus-visible,.resend:focus-visible,.close:focus-visible,.page-link:focus-visible,.link-field:focus-visible{outline:2px solid #092e3e;outline-offset:3px}
.primary:focus-visible{box-shadow:0 0 0 2px #fff inset}
.sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}
.ce[data-state=closing]{pointer-events:none}
@media (min-width:600px){.card{padding:56px 44px 26px}.title{margin-top:20px}}
/* Too little height (a small phone with the keyboard or the cookie notice up, a phone on its side): drop the icon, keep every line. */
.ce[data-fit=tight] .card{padding:22px 24px 14px}
.ce[data-fit=tight] .icon{display:none}
.ce[data-fit=tight] .title{margin:0 36px 8px;font-size:28px}
.ce[data-fit=tight] .line{font-size:17px}
.ce[data-fit=tight] .primary{min-height:52px;margin-top:14px}
.ce[data-fit=tight] .code{margin-top:14px}
.ce[data-fit=tight] .hint{margin-top:10px}
.ce[data-fit=tight] .more{margin-top:10px;padding-top:4px}
@media (prefers-reduced-motion:no-preference){
.ce[data-state=open] .backdrop{animation:ce-fade .2s ease-out both}
.ce[data-state=open] .card{animation:ce-rise .36s cubic-bezier(.23,1,.32,1) both}
.ce[data-state=open] .icon{animation:ce-pop .45s cubic-bezier(.34,1.4,.64,1) .08s both}
.ce[data-state=closing] .backdrop,.ce[data-state=closing] .card{animation:ce-out .16s ease-in both}
.primary:active,.secondary:active{transform:scale(.985)}
}
@keyframes ce-fade{from{opacity:0}to{opacity:1}}
@keyframes ce-out{from{opacity:1}to{opacity:0}}
@keyframes ce-rise{from{opacity:0;transform:translateY(18px) scale(.97)}to{opacity:1;transform:none}}
@keyframes ce-pop{from{opacity:0;transform:scale(.6)}to{opacity:1;transform:none}}
`
// Scroll lock the way the homepage's report viewer does it (body.report-open), and the page under it never reflows.
const PAGE_STYLE = 'body.aelo-check-email-open{overflow:hidden}'
const ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="3" y="5.5" width="18" height="13" rx="1.6"/><path d="m3.6 6.6 8.4 6.4 8.4-6.4"/></svg>'
const DONE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>'
const CLOSE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>'
const ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M7 17 17 7M9 7h8v8"/></svg>'
// The cookie notice and the privacy controls (privacy-analytics.css) and the containers they mount in.
const PRIVACY = '.aelo-privacy-panel, .aelo-privacy-settings'
const PRIVACY_ROOTS = '#aelo-static-privacy, #aelo-lifestyle-privacy'

let current: { close: (restore?: boolean) => void } | null = null
let pageStyle: HTMLStyleElement | null = null

/** The element that really has focus, inside open shadow roots too (Ask Aelo's panel lives in one). */
export function deepActiveElement(): Element | null {
  let active: Element | null = document.activeElement
  while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement
  return active === document.body ? null : active
}

function focusable(element: Element | null): element is HTMLElement {
  return element instanceof HTMLElement && element.isConnected && !(element as HTMLButtonElement).disabled
    && !element.closest('[inert]') && element.getClientRects().length > 0
}

function privacyControl(node: EventTarget | null) {
  return node instanceof Element && Boolean(node.closest(`${PRIVACY}, ${PRIVACY_ROOTS}`))
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

export function closeCheckEmailDialog() { current?.close(false) }

export function openCheckEmailDialog(options: CheckEmailOptions) {
  current?.close(false)
  const userAgent = options.userAgent ?? navigator.userAgent
  const inbox = inboxTarget(options.email, userAgent)
  // The page passes onVerify only when the server says this email carries a code (new or returning, still unconfirmed).
  const withCode = Boolean(options.onVerify)
  const id = `aelo-check-email-${Math.random().toString(36).slice(2, 8)}`
  // Where focus returns: the dialog the person joined from (Ask Aelo), if it is still there; otherwise the form.
  const originDialog = options.origin?.closest('[role=dialog]') ?? null

  const host = document.createElement('div')
  host.id = 'aelo-check-email'
  host.className = 'ph-no-capture'
  host.setAttribute('data-private', '')
  const shadow = host.attachShadow({ mode: 'open' })
  const style = document.createElement('style')
  style.textContent = STYLE
  const root = element('div', 'ce')
  root.dataset.state = 'open'
  const backdrop = element('div', 'backdrop')
  const card = element('section', 'card')
  card.setAttribute('role', 'dialog')
  card.setAttribute('aria-modal', 'true')
  card.setAttribute('aria-labelledby', `${id}-title`)
  card.setAttribute('aria-describedby', `${id}-line`)
  card.tabIndex = -1

  const close = element('button', 'close')
  close.type = 'button'
  close.setAttribute('aria-label', COPY.close)
  close.innerHTML = CLOSE
  const icon = element('div', 'icon')
  icon.innerHTML = ICON
  const title = element('h2', 'title', COPY.title)
  title.id = `${id}-title`
  const line = element('p', 'line')
  line.id = `${id}-line`
  const [before, after] = withCode ? COPY.lineCode : options.member === 'existing' ? COPY.lineExisting : COPY.lineNew
  const address = document.createElement('strong')
  for (const part of options.email.split(/(?<=[@.])/)) address.append(part, document.createElement('wbr'))
  const stop = after.match(/^[.,]/)?.[0] ?? ''
  const unit = element('span', 'addr')
  unit.append(address, stop)
  line.append(before, unit, after.slice(stop.length))
  card.append(close, icon, title, line)

  // ---- the inbox button: the main button without a code; a second, outlined button under the code box with one
  const note = element('p', 'note')
  note.setAttribute('role', 'status')
  let inboxButton: HTMLAnchorElement | HTMLButtonElement
  if (inbox) {
    const link = element('a', withCode ? 'secondary' : 'primary')
    const app = inbox.app
    link.href = app ?? inbox.url
    if (!app) { link.target = '_blank'; link.rel = 'noopener noreferrer' }
    link.append(element('span', undefined, inbox.label))
    link.insertAdjacentHTML('beforeend', ARROW)
    if (!app) link.append(element('span', 'sr', ` ${COPY.newTab}`))
    link.addEventListener('click', () => {
      options.onInbox?.(inbox.provider)
      if (!app) {
        // The web inbox opens in a new tab; without a code to type, the pop-up has done its job.
        if (!withCode) setTimeout(() => finish(true), 0)
        return
      }
      // The app either opens (this page goes to the background) or nothing happens: then say what to do, here.
      let left = false
      const away = () => { if (document.visibilityState === 'hidden') left = true }
      document.addEventListener('visibilitychange', away)
      setTimeout(() => {
        document.removeEventListener('visibilitychange', away)
        if (!left && host.isConnected) note.textContent = COPY.appMissing
      }, 1600)
    })
    inboxButton = link
  } else {
    inboxButton = element('button', 'primary', COPY.gotIt)
    inboxButton.type = 'button'
    inboxButton.addEventListener('click', () => finish(true))
  }

  // ---- the code box (new signups): type or paste the 6 digits; it checks itself at the sixth
  let focusTarget: HTMLElement = inboxButton
  let codeInput: HTMLInputElement | null = null
  let codeStatus: HTMLParagraphElement | null = null
  if (withCode) {
    const form = element('form', 'code')
    form.noValidate = true
    const label = element('label', undefined, COPY.codeLabel)
    label.htmlFor = `${id}-code`
    const input = element('input')
    input.id = `${id}-code`
    input.type = 'text'
    input.inputMode = 'numeric'
    input.autocomplete = 'one-time-code'
    input.pattern = '[0-9]*'
    input.maxLength = 6
    input.placeholder = '______'
    input.spellcheck = false
    input.setAttribute('aria-describedby', `${id}-code-status ${id}-code-hint`)
    const confirm = element('button', 'primary', COPY.confirm)
    confirm.type = 'submit'
    confirm.disabled = true
    const status = element('p', 'status')
    status.id = `${id}-code-status`
    status.setAttribute('role', 'status')
    status.setAttribute('aria-live', 'polite')
    const hint = element('p', 'hint', COPY.codeHint)
    hint.id = `${id}-code-hint`
    form.append(label, input, confirm, status)
    card.append(form, hint)
    if (inbox) card.append(inboxButton)
    card.append(note)
    codeInput = input
    codeStatus = status
    focusTarget = input

    let checking = false
    const showError = (message: string) => {
      status.textContent = message
      status.dataset.tone = 'error'
      input.setAttribute('aria-invalid', 'true')
    }
    const check = async () => {
      const code = cleanCode(input.value)
      if (code.length !== 6 || checking || closed) return
      checking = true
      input.readOnly = true
      confirm.disabled = true
      confirm.textContent = COPY.checking
      delete status.dataset.tone
      status.textContent = ''
      let result: CodeOutcome
      try { result = await options.onVerify!(code) } catch { result = { outcome: 'failed', message: COPY.failed } }
      if (closed) return
      checking = false
      input.readOnly = false
      confirm.textContent = COPY.confirm
      if (result.outcome === 'ok') { void showDone(); return }
      if (result.outcome === 'invalid') {
        const left = result.attemptsLeft
        showError(left == null ? COPY.wrongCode : `${COPY.wrongCode} ${left === 1 ? COPY.oneTryLeft : `${left}${COPY.triesLeft}`}`)
      } else if (result.outcome === 'expired') showError(COPY.expired)
      else if (result.outcome === 'limited') showError(COPY.limited)
      else showError(result.message || COPY.failed)
      input.value = ''
      confirm.disabled = true
      input.focus()
    }
    input.addEventListener('input', () => {
      const code = cleanCode(input.value)
      if (code !== input.value) input.value = code
      input.removeAttribute('aria-invalid')
      confirm.disabled = code.length !== 6
      if (code.length === 6) void check()
    })
    form.addEventListener('submit', event => { event.preventDefault(); void check() })
  } else {
    const hint = element('p', 'hint', COPY.hint)
    card.append(inboxButton, hint, note)
  }

  // ---- "Send a new code" / "Send it again": the form's own request, so the form's line updates too
  if (options.onResend) {
    const more = element('div', 'more')
    if (withCode) more.append(element('p', 'hint', COPY.hint))
    const resend = element('button', 'resend', withCode ? COPY.resendCode : COPY.resend)
    resend.type = 'button'
    const status = element('p', 'status')
    status.setAttribute('role', 'status')
    status.setAttribute('aria-live', 'polite')
    resend.addEventListener('click', async () => {
      if (resend.disabled) return
      resend.disabled = true
      resend.textContent = COPY.sending
      delete status.dataset.tone
      status.textContent = ''
      try {
        const result = await options.onResend!()
        if (!result || !host.isConnected) return
        status.textContent = result.outcome === 'sent' ? COPY.resent : result.message
        if (result.outcome === 'failed') status.dataset.tone = 'error'
        if (result.outcome === 'sent' && codeInput && codeStatus) {
          // A new email carries a new code; the old one still works until it expires, so just clear the error.
          codeStatus.textContent = ''
          codeInput.removeAttribute('aria-invalid')
        }
      } finally {
        resend.disabled = false
        resend.textContent = withCode ? COPY.resendCode : COPY.resend
      }
    })
    more.append(resend, status)
    card.append(more)
  }
  root.append(backdrop, card)
  shadow.append(style, root)

  // ---- step two: "You're in!" and the share step, in the same card (the person is still inside Facebook here)
  async function showDone() {
    let share: ShareStep | null = null
    try { share = (await options.onConfirmed?.()) ?? null } catch { share = null }
    if (closed) return
    const doneIcon = element('div', 'icon')
    doneIcon.dataset.done = ''
    doneIcon.innerHTML = DONE
    title.textContent = COPY.doneTitle
    line.textContent = share ? COPY.doneLine : COPY.doneNoLink
    const parts: Node[] = [close, doneIcon, title, line]
    let firstAction: HTMLElement
    if (share) {
      const ready = share
      const dots = element('div', 'dots')
      dots.setAttribute('aria-hidden', 'true')
      for (let index = 0; index < 5; index++) dots.append(element('span'))
      const count = element('p', 'count', COPY.doneCount)
      const shareButton = element('button', 'primary', COPY.shareText)
      shareButton.type = 'button'
      const field = element('input', 'link-field')
      field.readOnly = true
      field.value = ready.link
      field.setAttribute('aria-label', COPY.linkLabel)
      field.addEventListener('focus', () => field.select())
      const copy = element('button', 'secondary', COPY.copyLink)
      copy.type = 'button'
      const copyStatus = element('p', 'status')
      copyStatus.setAttribute('role', 'status')
      copyStatus.setAttribute('aria-live', 'polite')
      const copyLink = async () => {
        try {
          await navigator.clipboard.writeText(ready.link)
          copyStatus.textContent = COPY.copied
          options.onShare?.('copy')
        } catch {
          field.focus()
          field.select()
          copyStatus.textContent = COPY.copyFailed
        }
      }
      shareButton.addEventListener('click', async () => {
        const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> }
        if (typeof nav.share === 'function') {
          try {
            await nav.share({ text: ready.message })
            options.onShare?.('native')
          } catch (error) {
            // Closing the share sheet is a choice, not a failure.
            if ((error as { name?: string } | null)?.name !== 'AbortError') void copyLink()
          }
          return
        }
        if (platformOf(userAgent)) {
          options.onShare?.('sms')
          location.href = `sms:?&body=${encodeURIComponent(ready.message)}`
          return
        }
        void copyLink()
      })
      copy.addEventListener('click', () => { void copyLink() })
      const page = element('a', 'page-link', COPY.myPage)
      page.href = ready.pageUrl
      parts.push(dots, count, shareButton, field, copy, copyStatus, page)
      firstAction = shareButton
    } else {
      const page = element('a', 'primary', COPY.myPage)
      page.href = '/founding/refer'
      parts.push(page)
      firstAction = page
    }
    card.replaceChildren(...parts)
    card.setAttribute('aria-describedby', `${id}-line`)
    focusTarget = firstAction
    root.dataset.fit = 'full'
    fullHeight = 0
    reserve()
    firstAction.focus({ preventScroll: true })
    card.scrollTop = 0
    if (share) options.onShareShown?.()
  }

  // ---- the cookie choice stays visible and usable: lay the card out above the notice and the privacy controls
  let fullHeight = 0
  function reserve() {
    let bottom = 0, right = 0
    const shown = (selector: string) => [...document.querySelectorAll<HTMLElement>(selector)].map(node => node.getBoundingClientRect())
      .filter(rect => rect.width && rect.height && rect.top < innerHeight)
    for (const rect of shown('.aelo-privacy-settings')) bottom = Math.max(bottom, innerHeight - rect.top)
    for (const rect of shown('.aelo-privacy-panel')) {
      // Laptops show the notice as a card in the corner: a centred pop-up that already clears it stays centred.
      const cardWidth = card.offsetWidth, cardHeight = Math.min(card.scrollHeight, innerHeight - 32 - bottom)
      const left = (innerWidth - cardWidth) / 2, top = 16 + (innerHeight - 32 - bottom - cardHeight) / 2
      if (left + cardWidth <= rect.left - 16 || left >= rect.right + 16 || top + cardHeight <= rect.top - 16 || top >= rect.bottom + 16) continue
      // Otherwise the pop-up sits above the notice; when that space is too short for it but the space beside it is wide
      // enough (smaller laptops, phones on their side), it moves left only as far as it must to clear the notice.
      const roomAbove = rect.top - 32, roomBeside = rect.left - 32
      if (rect.left > 0 && roomAbove < card.scrollHeight && roomBeside >= 320 && roomBeside > roomAbove) {
        right = Math.max(right, Math.max(0, Math.min(innerWidth - rect.left, innerWidth + cardWidth - 2 * (rect.left - 16))))
      } else bottom = Math.max(bottom, innerHeight - rect.top)
    }
    bottom = Math.round(Math.min(bottom, innerHeight * 0.6))
    root.style.setProperty('--ce-reserve', `${bottom}px`)
    root.style.setProperty('--ce-reserve-right', `${Math.round(right)}px`)
    // The full layout's height is measured while it is showing; the tight one is used while that height doesn't fit.
    if (root.dataset.fit !== 'tight') fullHeight = card.scrollHeight
    root.dataset.fit = fullHeight > innerHeight - bottom - 32 ? 'tight' : 'full'
  }
  const privacyWatch = new MutationObserver(() => {
    reserve()
    // A cookie choice made with the notice focused removes the notice; focus comes back to the pop-up, not the page.
    if (!document.activeElement || document.activeElement === document.body) focusTarget.focus({ preventScroll: true })
  })
  for (const node of document.querySelectorAll(PRIVACY_ROOTS)) privacyWatch.observe(node, { childList: true, subtree: true, attributes: true })

  // ---- everything else is inert while it is open (never the privacy controls); restored exactly on close
  const madeInert: HTMLElement[] = []
  const keepActive = (node: Element) => node === host || node.matches(PRIVACY_ROOTS) || Boolean(node.querySelector(PRIVACY))
  const makeInert = (node: Node) => {
    if (!(node instanceof HTMLElement) || node.inert || keepActive(node) || /^(SCRIPT|STYLE|LINK|META|TEMPLATE|NOSCRIPT)$/.test(node.tagName)) return
    node.inert = true
    madeInert.push(node)
  }
  const lateNodes = new MutationObserver(records => { for (const record of records) record.addedNodes.forEach(makeInert) })

  // Tab cycles through the dialog's own controls and then the cookie choice, which stays usable, and back again.
  function cycle() {
    const usable = (node: HTMLElement) => !(node as HTMLButtonElement).disabled && node.getClientRects().length > 0
    const own = [...card.querySelectorAll<HTMLElement>('button, a[href], input')].filter(usable)
    const privacy = [...document.querySelectorAll<HTMLElement>(PRIVACY_ROOTS)]
      .flatMap(node => [...node.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea')]).filter(usable)
    return [...own, ...privacy]
  }
  function onKey(event: KeyboardEvent) {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); finish(true); return }
    if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return
    const items = cycle()
    if (!items.length) return
    event.preventDefault()
    const index = items.indexOf((shadow.activeElement ?? deepActiveElement()) as HTMLElement)
    const next = index < 0 ? (event.shiftKey ? items[items.length - 1] : items[0]) : items[(index + (event.shiftKey ? items.length - 1 : 1)) % items.length]
    next.focus()
  }
  function onFocusIn(event: FocusEvent) {
    if (event.target === host || privacyControl(event.target)) return
    focusTarget.focus({ preventScroll: true })
  }
  // The backdrop closes it; a press that starts on the card (selecting the address, say) and ends outside does not.
  let pressedOutside = false
  root.addEventListener('pointerdown', event => { pressedOutside = !card.contains(event.target as Node) })
  root.addEventListener('click', event => {
    if (pressedOutside && !card.contains(event.target as Node)) finish(true)
    pressedOutside = false
  })
  close.addEventListener('click', () => finish(true))

  let closed = false
  function finish(restore: boolean) {
    if (closed) return
    closed = true
    document.removeEventListener('keydown', onKey, true)
    document.removeEventListener('focusin', onFocusIn, true)
    removeEventListener('resize', reserve)
    privacyWatch.disconnect()
    lateNodes.disconnect()
    madeInert.splice(0).forEach(node => { node.inert = false })
    document.body.classList.remove('aelo-check-email-open')
    if (current === handle) current = null
    if (restore) {
      const back = options.origin && !options.returnFocus.closest('form')?.contains(options.origin) && focusable(options.origin)
        ? options.origin
        : originDialog?.isConnected && focusable(originDialog.querySelector('[tabindex="-1"]'))
          ? originDialog.querySelector<HTMLElement>('[tabindex="-1"]')!
          : options.returnFocus
      if (back === options.returnFocus && !back.hasAttribute('tabindex')) back.tabIndex = -1
      back.focus()
    }
    const remove = () => host.remove()
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !restore) { remove(); return }
    root.dataset.state = 'closing'
    root.addEventListener('animationend', remove, { once: true })
    setTimeout(remove, 220)
  }
  const handle = { close: (restore = false) => finish(restore) }
  current = handle

  if (!pageStyle?.isConnected) {
    pageStyle = document.createElement('style')
    pageStyle.textContent = PAGE_STYLE
    document.head.append(pageStyle)
  }
  document.body.append(host)
  document.body.classList.add('aelo-check-email-open')
  for (const node of [...document.body.children]) makeInert(node)
  lateNodes.observe(document.body, { childList: true })
  reserve()
  addEventListener('resize', reserve)
  document.addEventListener('keydown', onKey, true)
  document.addEventListener('focusin', onFocusIn, true)
  focusTarget.focus({ preventScroll: true })
  options.onShown?.(inbox?.provider ?? 'none')
  return handle
}
